import React, { useState, useEffect } from 'react';
import { 
  Typography, Box, Paper, Grid, Card, CardContent, Chip, 
  CircularProgress, Alert, Stack, FormControl, InputLabel, 
  Select, MenuItem, TextField, Tabs, Tab
} from '@mui/material';
import { 
  CheckCircle as GoodIcon, 
  Warning as AverageIcon, 
  Cancel as RejectedIcon,
  Phone as CallIcon,
  Assignment as QAIcon,
  Assessment as LeadsIcon
} from '@mui/icons-material';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  Cell
} from 'recharts';
import { useAuth } from '../contexts/AuthContext';
import { getCalls } from '../services/aiService';
import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

const Dashboard = () => {
  const { user, token } = useAuth();
  const [currentTab, setCurrentTab] = useState(0);
  const [dashboardData, setDashboardData] = useState({
    totalCalls: 0,
    qaEvaluated: 0,
    goodCalls: 0,
    averageCalls: 0,
    rejectedCalls: 0,
    loading: true,
    error: null
  });
  
  // Date filter state
  const [dateFilter, setDateFilter] = useState('all'); // 'today', 'all', 'custom'
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  
  // Leads Dashboard state
  const [leadsDateFilter, setLeadsDateFilter] = useState('today'); // Default to today
  const [leadsStartDate, setLeadsStartDate] = useState(new Date().toISOString().split('T')[0]);
  const [leadsEndDate, setLeadsEndDate] = useState(new Date().toISOString().split('T')[0]);
  const [leadsAnalytics, setLeadsAnalytics] = useState({
    listDispo: [],
    listPerformance: [],
    loading: false,
    error: null
  });

  const fetchDashboardData = async () => {
    try {
      setDashboardData(prev => ({ ...prev, loading: true, error: null }));
      
      // Build query parameters based on date filter
      const params = { 
        page: 1, 
        limit: 10000 // Large number to get all calls
      };
      
      if (dateFilter === 'today') {
        const today = new Date().toISOString().split('T')[0];
        params.start_date = today;
        params.end_date = today;
      } else if (dateFilter === 'custom' && startDate && endDate) {
        params.start_date = startDate;
        params.end_date = endDate;
      }
      // For 'all', no date parameters are added
      
      // Fetch calls with date filtering
      const response = await getCalls(params);
      
      if (response && response.calls) {
        const calls = response.calls || [];
        
        // Calculate statistics
        const totalCalls = calls.length;
        const callsWithQA = calls.filter(call => call.qa_score);
        const qaEvaluated = callsWithQA.length;
        
        // Count QA ratings
        let goodCalls = 0;
        let averageCalls = 0;
        let rejectedCalls = 0;
        
        callsWithQA.forEach(call => {
          const score = call.qa_score;
          if (typeof score === 'string') {
            // New categorical system
            switch (score.toLowerCase()) {
              case 'good':
                goodCalls++;
                break;
              case 'average':
                averageCalls++;
                break;
              case 'rejected':
                rejectedCalls++;
                break;
            }
          } else if (typeof score === 'number') {
            // Old numerical system - convert to categories
            if (score >= 80) {
              goodCalls++;
            } else if (score >= 60) {
              averageCalls++;
            } else {
              rejectedCalls++;
            }
          }
        });
        
        setDashboardData({
          totalCalls,
          qaEvaluated,
          goodCalls,
          averageCalls,
          rejectedCalls,
          loading: false,
          error: null
        });
      } else {
        setDashboardData(prev => ({ 
          ...prev, 
          loading: false, 
          error: 'Failed to fetch dashboard data' 
        }));
      }
    } catch (error) {
      console.error('Dashboard data fetch error:', error);
      setDashboardData(prev => ({ 
        ...prev, 
        loading: false, 
        error: error.message || 'Failed to fetch dashboard data' 
      }));
    }
  };

  useEffect(() => {
    fetchDashboardData();
  }, [dateFilter, startDate, endDate]);

  useEffect(() => {
    if (currentTab === 1) {
      fetchLeadsAnalytics();
    }
  }, [currentTab, leadsDateFilter, leadsStartDate, leadsEndDate]);

  const fetchLeadsAnalytics = async () => {
    setLeadsAnalytics(prev => ({ ...prev, loading: true, error: null }));
    try {
      const params = new URLSearchParams();
      
      // Add date filtering based on leads dashboard filter
      if (leadsDateFilter === 'today') {
        const today = new Date().toISOString().split('T')[0];
        params.append('startDate', today);
        params.append('endDate', today);
      } else if (leadsDateFilter === 'custom') {
        if (leadsStartDate) params.append('startDate', leadsStartDate);
        if (leadsEndDate) params.append('endDate', leadsEndDate);
      }
      // If 'all', don't add date parameters
      
      const response = await axios.get(`${API_BASE_URL}/leads/analytics?${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data.success && response.data.data) {
        const data = response.data.data;
        setLeadsAnalytics({
          listDispo: data.listDispo || [],
          listPerformance: data.listPerformance || [],
          loading: false,
          error: null
        });
      } else {
        setLeadsAnalytics({
          listDispo: [],
          listPerformance: [],
          loading: false,
          error: 'Failed to fetch leads analytics'
        });
      }
    } catch (error) {
      console.error('Error fetching leads analytics:', error);
      setLeadsAnalytics({
        listDispo: [],
        listPerformance: [],
        loading: false,
        error: error.message || 'Failed to fetch leads analytics'
      });
    }
  };

  const StatCard = ({ title, value, icon, color = 'primary', description }) => (
    <Card sx={{ height: '100%' }}>
      <CardContent>
        <Stack direction="row" alignItems="center" spacing={2}>
          <Box sx={{ color: `${color}.main` }}>
            {icon}
          </Box>
          <Box sx={{ flexGrow: 1 }}>
            <Typography variant="h4" component="div" sx={{ fontWeight: 'bold' }}>
              {value}
            </Typography>
            <Typography variant="h6" color="text.secondary">
              {title}
            </Typography>
            {description && (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                {description}
              </Typography>
            )}
          </Box>
        </Stack>
      </CardContent>
    </Card>
  );

  const QACard = ({ title, value, icon, color, percentage }) => (
    <Card sx={{ height: '100%' }}>
      <CardContent>
        <Stack direction="row" alignItems="center" spacing={2}>
          <Box sx={{ color: color }}>
            {icon}
          </Box>
          <Box sx={{ flexGrow: 1 }}>
            <Typography variant="h3" component="div" sx={{ fontWeight: 'bold' }}>
              {value}
            </Typography>
            <Typography variant="h6" color="text.secondary">
              {title}
            </Typography>
            <Chip 
              label={`${percentage}% of QA evaluated`} 
              size="small" 
              sx={{ mt: 1 }}
              variant="outlined"
            />
          </Box>
        </Stack>
      </CardContent>
    </Card>
  );

  if (dashboardData.loading) {
    return (
      <Box sx={{ p: 3, display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (dashboardData.error) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">
          {dashboardData.error}
        </Alert>
      </Box>
    );
  }

  const qaPercentage = dashboardData.qaEvaluated > 0 ? 
    ((dashboardData.qaEvaluated / dashboardData.totalCalls) * 100).toFixed(1) : 0;

  // Process leads analytics data for charts
  const processLeadsData = () => {
    // Use listPerformance if available for accurate total leads, otherwise fall back to listDispo
    if (leadsAnalytics.listPerformance && leadsAnalytics.listPerformance.length > 0) {
      // Use listPerformance which should have total_leads per list_id
      const listIdMap = {};
      
      // First, get total leads from listPerformance
      leadsAnalytics.listPerformance.forEach(row => {
        const listId = row.list_id;
        // Skip null or undefined list_ids
        if (!listId) return;
        
        // Parse total_leads as integer (handle string numbers from SQL)
        const totalLeads = parseInt(row.total_leads, 10) || 0;
        
        if (!listIdMap[listId]) {
          listIdMap[listId] = {
            listId: listId,
            totalLeads: totalLeads,
            sales: 0
          };
        } else {
          // If list_id already exists, use the larger total (shouldn't happen, but just in case)
          listIdMap[listId].totalLeads = Math.max(listIdMap[listId].totalLeads, totalLeads);
        }
      });
      
      // Then, get sales count from listDispo
      if (leadsAnalytics.listDispo && leadsAnalytics.listDispo.length > 0) {
        leadsAnalytics.listDispo.forEach(row => {
          const listId = row.list_id;
          // Skip null or undefined list_ids
          if (!listId) return;
          
          const count = parseInt(row.count, 10) || 0;
          
          if (listIdMap[listId] && row.disposition === 'SALE') {
            listIdMap[listId].sales = count;
          }
        });
      }
      
      // Convert to array and calculate conversion rate
      return Object.values(listIdMap).map(item => ({
        listId: item.listId,
        totalLeads: item.totalLeads,
        sales: item.sales,
        conversionRate: item.totalLeads > 0 
          ? ((item.sales / item.totalLeads) * 100).toFixed(2) 
          : '0.00'
      })).sort((a, b) => b.totalLeads - a.totalLeads); // Sort by total leads descending
    }
    
    // Fallback to listDispo if listPerformance is not available
    if (!leadsAnalytics.listDispo || leadsAnalytics.listDispo.length === 0) {
      return [];
    }

    // Group by list_id from listDispo (fallback when listPerformance is not available)
    const listIdMap = {};
    leadsAnalytics.listDispo.forEach(row => {
      const listId = row.list_id;
      const count = parseInt(row.count, 10) || 0;
      
      if (!listIdMap[listId]) {
        listIdMap[listId] = {
          listId: listId,
          totalLeads: 0,
          sales: 0,
          otherDispositions: {}
        };
      }
      listIdMap[listId].totalLeads += count;
      if (row.disposition === 'SALE') {
        listIdMap[listId].sales = count;
      } else {
        listIdMap[listId].otherDispositions[row.disposition] = count;
      }
    });

    // Convert to array and calculate conversion rate
    return Object.values(listIdMap).map(item => ({
      listId: item.listId,
      totalLeads: item.totalLeads,
      sales: item.sales,
      conversionRate: item.totalLeads > 0 
        ? ((item.sales / item.totalLeads) * 100).toFixed(2) 
        : '0.00'
    })).sort((a, b) => b.totalLeads - a.totalLeads); // Sort by total leads descending
  };

  const leadsChartData = processLeadsData();

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <Paper sx={{ p: 1.5 }}>
          <Typography variant="body2" fontWeight="bold">{`List ID: ${label}`}</Typography>
          {payload.map((entry, index) => (
            <Typography key={index} variant="body2" style={{ color: entry.color }}>
              {`${entry.name}: ${entry.value}${entry.name === 'Conversion Rate' ? '%' : ''}`}
            </Typography>
          ))}
        </Paper>
      );
    }
    return null;
  };

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" gutterBottom>
        📊 Dashboard
      </Typography>
      
      <Typography variant="h6" color="text.secondary" gutterBottom sx={{ mb: 3 }}>
        Welcome back, {user?.name || 'User'}!
      </Typography>

      {/* Tabs */}
      <Paper sx={{ mb: 3 }}>
        <Tabs 
          value={currentTab} 
          onChange={(e, newValue) => setCurrentTab(newValue)}
          indicatorColor="primary"
          textColor="primary"
        >
          <Tab label="QA Dashboard" icon={<QAIcon />} iconPosition="start" />
          <Tab label="Leads Dashboard" icon={<LeadsIcon />} iconPosition="start" />
        </Tabs>
      </Paper>

      {/* QA Dashboard Tab */}
      {currentTab === 0 && (
        <>

      {/* Date Filter Controls */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={6} md={3}>
            <FormControl fullWidth size="small">
              <InputLabel>Date Filter</InputLabel>
              <Select
                value={dateFilter}
                label="Date Filter"
                onChange={(e) => setDateFilter(e.target.value)}
              >
                <MenuItem value="today">Today Only</MenuItem>
                <MenuItem value="all">All Time</MenuItem>
                <MenuItem value="custom">Custom Date Range</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          
          {dateFilter === 'custom' && (
            <>
              <Grid item xs={12} sm={6} md={3}>
                <TextField
                  fullWidth
                  size="small"
                  label="Start Date"
                  type="date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <TextField
                  fullWidth
                  size="small"
                  label="End Date"
                  type="date"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
            </>
          )}
          
          <Grid item xs={12} sm={6} md={3}>
            <Typography variant="body2" color="text.secondary">
              {dateFilter === 'today' && 'Showing today\'s data only'}
              {dateFilter === 'all' && 'Showing all time data'}
              {dateFilter === 'custom' && startDate && endDate && 
                `Showing data from ${startDate} to ${endDate}`}
              {dateFilter === 'custom' && (!startDate || !endDate) && 
                'Please select date range'}
            </Typography>
          </Grid>
        </Grid>
      </Paper>

      {/* Overview Stats */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Total Calls"
            value={dashboardData.totalCalls}
            icon={<CallIcon sx={{ fontSize: 40 }} />}
            color="primary"
            description="All calls in the system"
          />
        </Grid>
        
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="QA Evaluated"
            value={dashboardData.qaEvaluated}
            icon={<QAIcon sx={{ fontSize: 40 }} />}
            color="info"
            description={`${qaPercentage}% of total calls`}
          />
        </Grid>
        
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Pending QA"
            value={dashboardData.totalCalls - dashboardData.qaEvaluated}
            icon={<QAIcon sx={{ fontSize: 40 }} />}
            color="warning"
            description="Calls awaiting QA evaluation"
          />
        </Grid>
        
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Rejected Calls"
            value={dashboardData.rejectedCalls}
            icon={<RejectedIcon sx={{ fontSize: 40 }} />}
            color="error"
            description="Calls requiring immediate attention"
          />
        </Grid>
      </Grid>

      {/* QA Breakdown */}
      <Typography variant="h5" gutterBottom sx={{ mt: 4, mb: 2 }}>
        Quality Assessment Breakdown
      </Typography>
      
      <Grid container spacing={3}>
        <Grid item xs={12} sm={4}>
          <QACard
            title="Good Quality"
            value={dashboardData.goodCalls}
            icon={<GoodIcon sx={{ fontSize: 50 }} />}
            color="#4caf50"
            percentage={dashboardData.qaEvaluated > 0 ? 
              ((dashboardData.goodCalls / dashboardData.qaEvaluated) * 100).toFixed(1) : 0}
          />
        </Grid>
        
        <Grid item xs={12} sm={4}>
          <QACard
            title="Average Quality"
            value={dashboardData.averageCalls}
            icon={<AverageIcon sx={{ fontSize: 50 }} />}
            color="#ff9800"
            percentage={dashboardData.qaEvaluated > 0 ? 
              ((dashboardData.averageCalls / dashboardData.qaEvaluated) * 100).toFixed(1) : 0}
          />
        </Grid>
        
        <Grid item xs={12} sm={4}>
          <QACard
            title="Rejected Calls"
            value={dashboardData.rejectedCalls}
            icon={<RejectedIcon sx={{ fontSize: 50 }} />}
            color="#f44336"
            percentage={dashboardData.qaEvaluated > 0 ? 
              ((dashboardData.rejectedCalls / dashboardData.qaEvaluated) * 100).toFixed(1) : 0}
          />
        </Grid>
      </Grid>

      {/* Summary Message */}
      {dashboardData.rejectedCalls > 0 && (
        <Alert severity="warning" sx={{ mt: 3 }}>
          <Typography variant="body1">
            <strong>Attention Required:</strong> You have {dashboardData.rejectedCalls} rejected call{dashboardData.rejectedCalls > 1 ? 's' : ''} that may need immediate review and follow-up action.
          </Typography>
        </Alert>
      )}
      
      {dashboardData.qaEvaluated === 0 && dashboardData.totalCalls > 0 && (
        <Alert severity="info" sx={{ mt: 3 }}>
          <Typography variant="body1">
            <strong>Get Started:</strong> You have {dashboardData.totalCalls} call{dashboardData.totalCalls > 1 ? 's' : ''} ready for QA evaluation. Visit the Calls page to begin quality assessments.
          </Typography>
        </Alert>
      )}
        </>
      )}

      {/* Leads Dashboard Tab */}
      {currentTab === 1 && (
        <>
          <Typography variant="h5" gutterBottom sx={{ mb: 2 }}>
            Leads Dashboard
          </Typography>

          {/* Date Filter Controls */}
          <Paper sx={{ p: 2, mb: 3 }}>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} sm={6} md={3}>
                <FormControl fullWidth size="small">
                  <InputLabel>Date Filter</InputLabel>
                  <Select
                    value={leadsDateFilter}
                    label="Date Filter"
                    onChange={(e) => setLeadsDateFilter(e.target.value)}
                  >
                    <MenuItem value="today">Today Only</MenuItem>
                    <MenuItem value="all">All Time</MenuItem>
                    <MenuItem value="custom">Custom Date Range</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              
              {leadsDateFilter === 'custom' && (
                <>
                  <Grid item xs={12} sm={6} md={3}>
                    <TextField
                      fullWidth
                      size="small"
                      label="Start Date"
                      type="date"
                      value={leadsStartDate}
                      onChange={(e) => setLeadsStartDate(e.target.value)}
                      InputLabelProps={{ shrink: true }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <TextField
                      fullWidth
                      size="small"
                      label="End Date"
                      type="date"
                      value={leadsEndDate}
                      onChange={(e) => setLeadsEndDate(e.target.value)}
                      InputLabelProps={{ shrink: true }}
                    />
                  </Grid>
                </>
              )}
              
              <Grid item xs={12} sm={6} md={3}>
                <Typography variant="body2" color="text.secondary">
                  {leadsDateFilter === 'today' && `Showing: ${new Date().toLocaleDateString()}`}
                  {leadsDateFilter === 'all' && 'Showing: All Time'}
                  {leadsDateFilter === 'custom' && leadsStartDate && leadsEndDate && 
                    `Showing: ${new Date(leadsStartDate).toLocaleDateString()} - ${new Date(leadsEndDate).toLocaleDateString()}`}
                </Typography>
              </Grid>
            </Grid>
          </Paper>

          {leadsAnalytics.loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
              <CircularProgress />
            </Box>
          ) : leadsAnalytics.error ? (
            <Alert severity="error" sx={{ mb: 3 }}>
              {leadsAnalytics.error}
            </Alert>
          ) : leadsChartData.length === 0 ? (
            <Alert severity="info" sx={{ mb: 3 }}>
              No leads data available for the selected date range.
            </Alert>
          ) : (
            <>
              {/* List ID Performance Bar Chart */}
              <Card sx={{ mb: 3 }}>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    List ID Performance - Total Leads
                  </Typography>
                  <ResponsiveContainer width="100%" height={400}>
                    <BarChart
                      data={leadsChartData}
                      margin={{ top: 20, right: 30, left: 20, bottom: 60 }}
                    >
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis 
                        dataKey="listId" 
                        angle={-45}
                        textAnchor="end"
                        height={100}
                      />
                      <YAxis />
                      <Tooltip content={<CustomTooltip />} />
                      <Legend />
                      <Bar dataKey="totalLeads" fill="#1976d2" name="Total Leads" />
                      <Bar dataKey="sales" fill="#2e7d32" name="SALES" />
                    </BarChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>

              {/* Conversion Rate Bar Chart */}
              <Card sx={{ mb: 3 }}>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Conversion Rate by List ID (SALE / Total Leads %)
                  </Typography>
                  <ResponsiveContainer width="100%" height={400}>
                    <BarChart
                      data={leadsChartData}
                      margin={{ top: 20, right: 30, left: 20, bottom: 60 }}
                    >
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis 
                        dataKey="listId" 
                        angle={-45}
                        textAnchor="end"
                        height={100}
                      />
                      <YAxis 
                        label={{ value: 'Conversion Rate (%)', angle: -90, position: 'insideLeft' }}
                      />
                      <Tooltip content={<CustomTooltip />} />
                      <Legend />
                      <Bar dataKey="conversionRate" fill="#2e7d32" name="Conversion Rate (%)">
                        {leadsChartData.map((entry, index) => {
                          const rate = parseFloat(entry.conversionRate);
                          let color = '#f44336'; // Red default (< 0.5%)
                          if (rate > 1) {
                            color = '#2e7d32'; // Green (> 1%)
                          } else if (rate >= 0.5) {
                            color = '#ff9800'; // Orange (0.5% - 0.99%)
                          }
                          return (
                            <Cell 
                              key={`cell-${index}`} 
                              fill={color} 
                            />
                          );
                        })}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>

              {/* Summary Table */}
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    List ID Summary
                  </Typography>
                  <Grid container spacing={2} sx={{ mt: 1 }}>
                    {leadsChartData.map((item, index) => (
                      <Grid item xs={12} sm={6} md={4} key={item.listId}>
                        <Paper sx={{ p: 2, border: '1px solid #e0e0e0' }}>
                          <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                            List ID: {item.listId}
                          </Typography>
                          <Stack spacing={1}>
                            <Box display="flex" justifyContent="space-between">
                              <Typography variant="body2">Total Leads:</Typography>
                              <Typography variant="body2" fontWeight="bold">{item.totalLeads}</Typography>
                            </Box>
                            <Box display="flex" justifyContent="space-between">
                              <Typography variant="body2" color="success.main">SALES:</Typography>
                              <Typography variant="body2" fontWeight="bold" color="success.main">{item.sales}</Typography>
                            </Box>
                            <Box display="flex" justifyContent="space-between">
                              <Typography variant="body2">Conversion Rate:</Typography>
                              <Chip 
                                label={`${item.conversionRate}%`}
                                size="small"
                                color={(() => {
                                  const rate = parseFloat(item.conversionRate);
                                  if (rate > 1) return 'success'; // Green (> 1%)
                                  if (rate >= 0.5) return 'warning'; // Orange (0.5% - 0.99%)
                                  return 'error'; // Red (< 0.5%)
                                })()}
                              />
                            </Box>
                          </Stack>
                        </Paper>
                      </Grid>
                    ))}
                  </Grid>
                </CardContent>
              </Card>
            </>
          )}
        </>
      )}
    </Box>
  );
};

export default Dashboard; 